import { useEffect, useMemo } from 'react'
import { useAiAgentAvailability } from 'app/components/ai-agent-settings/useAiAgentAvailability'
import { typeSetup, type TypeSetupProvider } from 'app/lib/aiAgentAvailability'
import { useAIAgentStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import type { AppointmentTypeKey } from './locationAvailability.utils'

/** What the agent books for one card, and who the schedule lets take it. */
export type AppointmentTypeSetup = {
  typeName: string
  minutes?: number
  providers: TypeSetupProvider[]
  /** CareStack picks providers and chairs from its own schedule; this is its production type id. */
  carestackProductionTypeId?: string
}

/** The agent config field that holds each card's appointment type; hygiene books as the general type. */
const CONFIG_FIELD = {
  hygiene: `generalApptId`,
  limitedExam: `limitedExamApptId`,
  emergency: `emergencyApptId`,
  consult: `consultApptId`,
} as const satisfies Record<AppointmentTypeKey, string>

/**
 * Each availability card's setup for one location: the appointment type the agent books for it
 * and, from the schedule's availability records, which providers take it in which rooms. A card
 * whose category maps to no appointment type is `null`.
 */
export function useAppointmentTypeSetups(locationId: string): {
  loading: boolean
  setups: Record<AppointmentTypeKey, AppointmentTypeSetup | null>
  /** Today on the practice's calendar. */
  today: string
} {
  const agentStore = useAIAgentStore()
  const { records, names, appointmentTypes, activeProviderIds, loading, today } = useAiAgentAvailability(locationId)
  const config = agentStore.configsByLocationId.get(locationId)

  useEffect(() => {
    bg(agentStore.fetchConfig(locationId))
  }, [agentStore, locationId])

  const setups = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(CONFIG_FIELD).map(([key, field]) => {
          const type = appointmentTypes.find((candidate) => candidate.id === config?.[field])
          if (!type) return [key, null]
          return [
            key,
            {
              typeName: type.alias || type.displayName || type.name || `Appointment`,
              minutes: type.appointmentLength ?? undefined,
              providers: type.vendorId ? typeSetup(records, type.vendorId, names, activeProviderIds) : [],
              carestackProductionTypeId: type.vendor === `carestack` ? (type.vendorId ?? undefined) : undefined,
            },
          ]
        }),
      ) as Record<AppointmentTypeKey, AppointmentTypeSetup | null>,
    [config, appointmentTypes, records, names, activeProviderIds],
  )

  return { loading: loading || config === undefined, setups, today }
}
